import { useNavigate } from "react-router";

import { t } from "i18next";

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Typography
} from "@mui/material";

import { theme } from "@/src/theme/theme";

import { getAuctionEditRoute } from "@/src/constants/routes";
import { ConsumerAuctionDTO, VehicleType } from "@/src/types/types";
import { getAuctionImageUrl } from "@/src/utils/imageUtils";
import { addThousandsSeparator } from "@/src/utils/numberUtils";

interface DraftAuctionModalProps {
  open: boolean;
  onClose: () => void;
  draftAuction: ConsumerAuctionDTO | null;
  onContinue?: () => void;
  onStartNew?: () => void | Promise<void>;
  showContinueButton?: boolean;
  showStartNewButton?: boolean;
  isLoadingDetails?: boolean;
  isSubmitting?: boolean;
  /**
   * When false, onClose is not run after onContinue (use when onContinue
   * navigates away; default true for modals that only update local state).
   */
  closeAfterCustomContinue?: boolean;
}

type VehicleLike = NonNullable<ConsumerAuctionDTO["auctionItem"]>["vehicle"];

const buildVehicleDisplayTitle = (vehicle: VehicleLike | undefined): string => {
  if (!vehicle) {
    return "";
  }
  const reg = vehicle.regNo ? vehicle.regNo.toUpperCase() : "";
  const makeModel = `${vehicle.make || ""} ${vehicle.model || ""}`.trim();
  if (!makeModel) {
    return reg;
  }
  const sep = reg ? ", " : "";
  return `${reg}${sep}${makeModel}`;
};

const DraftAuctionSubmittingContent = () => (
  <Box
    aria-busy="true"
    aria-live="polite"
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      minHeight: 220,
      gap: 2,
      py: 2
    }}
  >
    <CircularProgress size={40} />
    <Typography
      variant="body2"
      sx={{
        color: theme.palette.text.secondary
      }}
    >
      {t("myAuctions.unfinishedDraftDialog.loading")}
    </Typography>
  </Box>
);

const DraftAuctionLoadingContent = () => (
  <Box aria-busy="true" aria-live="polite">
    <Skeleton
      variant="rectangular"
      width="100%"
      height={180}
      sx={{ borderRadius: 2, mb: 2 }}
    />
    <Skeleton variant="text" width="65%" height={32} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="90%" height={22} sx={{ mb: 1 }} />
    <Skeleton variant="text" width="75%" height={20} sx={{ mb: 2 }} />
    <Skeleton variant="text" width="100%" height={18} />
  </Box>
);

interface DraftVehicleCardProps {
  draftAuction: ConsumerAuctionDTO;
  showStartNewButton: boolean;
}

const DraftVehicleCard = (props: DraftVehicleCardProps) => {
  const { draftAuction, showStartNewButton } = props;
  const vehicle = draftAuction.auctionItem?.vehicle;
  const mileage = draftAuction.auctionItem?.auctionItemDetails?.mileage;
  const mileageSummary =
    mileage !== undefined && mileage !== null
      ? `${addThousandsSeparator(mileage.toString())} km`
      : null;
  const title = buildVehicleDisplayTitle(vehicle);
  const showTitle =
    vehicle?.regNo || vehicle?.make || vehicle?.model ? title : null;

  return (
    <Box>
      {draftAuction.auctionItem?.media &&
        draftAuction.auctionItem.media.length > 0 && (
          <Box
            sx={{
              width: "100%",
              height: { xs: 180, sm: 200 },
              mb: 2,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: theme.palette.misc.timelineBackground,
              position: "relative",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}
          >
            <Box
              component="img"
              src={getAuctionImageUrl(draftAuction, 0, "md", "4/3", true)}
              alt={`${vehicle?.make || ""} ${vehicle?.model || ""}`.trim()}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = "/assets/images/missing_car.jpg";
              }}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block"
              }}
            />
          </Box>
        )}
      {showTitle && (
        <Typography variant="h6" sx={{ mb: 1 }}>
          {showTitle}
        </Typography>
      )}
      {vehicle && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            mb: 2
          }}
        >
          {[
            vehicle.modelYear,
            mileageSummary,
            vehicle.fuelType,
            vehicle.gearType
          ]
            .filter(Boolean)
            .join(" • ")}
        </Typography>
      )}
      {showStartNewButton && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            mt: 2,
            mb: 1,
            fontStyle: "italic"
          }}
        >
          {t("myAuctions.unfinishedDraftDialog.createNewWillDelete")}
        </Typography>
      )}
    </Box>
  );
};

const renderDialogContent = (params: {
  isSubmitting: boolean;
  isLoadingDetails: boolean;
  draftAuction: ConsumerAuctionDTO | null;
  showStartNewButton: boolean;
}) => {
  const { isSubmitting, isLoadingDetails, draftAuction, showStartNewButton } =
    params;

  if (isSubmitting) {
    return <DraftAuctionSubmittingContent />;
  }

  if (isLoadingDetails) {
    return <DraftAuctionLoadingContent />;
  }

  if (draftAuction) {
    return (
      <DraftVehicleCard
        draftAuction={draftAuction}
        showStartNewButton={showStartNewButton}
      />
    );
  }

  return (
    <Box>
      <Typography variant="body2">
        {t("consumer.wizard.stepOwnerInfo.youAlreadyHaveAuction")}
      </Typography>
    </Box>
  );
};

const DraftAuctionModal = (props: DraftAuctionModalProps) => {
  const {
    open,
    onClose,
    draftAuction,
    onContinue,
    onStartNew,
    showContinueButton = true,
    showStartNewButton = true,
    isLoadingDetails = false,
    isSubmitting = false,
    closeAfterCustomContinue = true
  } = props;
  const navigate = useNavigate();

  const isBusy = isLoadingDetails || isSubmitting;

  const handleContinue = () => {
    if (onContinue) {
      onContinue();
      if (closeAfterCustomContinue) {
        onClose();
      }
      return;
    }
    if (draftAuction) {
      const vehicleType =
        draftAuction.auctionItem?.vehicle?.vehicleType ?? VehicleType.CAR;
      const editPath = getAuctionEditRoute(vehicleType);
      navigate(editPath);
    }
    onClose();
  };

  const handleStartNew = async () => {
    await Promise.resolve(onStartNew?.());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        transition: { timeout: { enter: 160, exit: 120 } }
      }}
    >
      <DialogTitle>
        {t("consumer.wizard.stepOwnerInfo.youAlreadyHaveAuction")}
      </DialogTitle>
      <DialogContent>
        {renderDialogContent({
          isSubmitting,
          isLoadingDetails,
          draftAuction,
          showStartNewButton
        })}
      </DialogContent>
      {!isSubmitting && (
        <DialogActions
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 1 },
            px: { xs: 2, sm: 2 },
            pb: { xs: 2, sm: 2 },
            justifyContent: { xs: "stretch", sm: "flex-end" },
            "& > button": {
              width: { xs: "100%", sm: "100%" },
              flex: { xs: "1 1 auto", sm: "1 1 0" }
            }
          }}
        >
          {showStartNewButton && (
            <Button
              onClick={handleStartNew}
              variant="outlined"
              fullWidth={true}
              disabled={isBusy}
              sx={{
                order: { xs: 2, sm: 1 },
                minHeight: { xs: "44px", sm: "44px" },
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                py: { xs: 1.5, sm: 1.5 },
                px: { xs: 3, sm: 3 }
              }}
            >
              {t("myAuctions.unfinishedDraftDialog.createNewAuction")}
            </Button>
          )}
          {showContinueButton && (draftAuction || isLoadingDetails) && (
            <Button
              variant="contained"
              onClick={handleContinue}
              fullWidth={true}
              disabled={isBusy}
              sx={{
                order: { xs: 1, sm: 2 },
                minHeight: { xs: "44px", sm: "44px" },
                fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                py: { xs: 1.5, sm: 1.5 },
                px: { xs: 3, sm: 3 }
              }}
            >
              {t("myAuctions.unfinishedDraftDialog.continueDraft")}
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default DraftAuctionModal;
